import { useState, useMemo, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Select, MenuItem, FormControl, TextField, Button, Box } from "@mui/material";
import { SlidersHorizontal } from "lucide-react";
import RefreshButton from "../../lib/components/RefreshButton";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import tz from "dayjs/plugin/timezone";
import MessageModal from "../../lib/components/MessageModal/MessageModal";
import ChargerDisplay from "../../lib/components/ChargerDisplay";
import MessageCell from "./MessageCell";
import "./styles.scss";
import Pagination from "../../lib/components/Pagination/Pagination";
import FeatureChip from "./FeatureChip";
import FeatureFilterSelect from "./FeatureFilterSelect";
import { fetchOcppMessages } from "./hooks/api";
import SlideOutPanel from "./SlideOutPanel";
import DataTable from "../../lib/components/DataTable";
import { useGlobalFilters } from "../../lib/context/GlobalFilterProvider";
import { MESSAGE_CONFIG, FEATURE_OPTIONS } from "../../lib/components/Filterbar/constants";
import Icon from "../../lib/components/Icons/Icon";
import { getLocalStorage, patchLocalStorage, LOCAL_STORAGE_KEYS } from "../../lib/utils/localStorageUtil";
import { getRange } from "../../lib/utils/utility";
import { DEFAULT_TIME_RANGE } from "../../lib/utils/constants";
import { renderMsgType } from "./utility";
import DateRangeToolbar from "../fleet_dashboard/DateRangeToolbar";
import useIsDarkTheme from "../../lib/hooks/useIsDarkTheme";

dayjs.extend(utc);
dayjs.extend(tz);

export default function OcppMessages({ embedded = true, activePreset, onPresetChange, onCustomChange, onRefresh }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalPayload, setModalPayload] = useState(null);
  const [modalIndex, setModalIndex] = useState(null);
  const isDark = useIsDarkTheme();
  const { serialNumber: globalSerialNumber, dateRange: globalDateRange, siteTimezone } = useGlobalFilters();
  const [serialNumber, setSerialNumber] = useState(globalSerialNumber);
  const [dateRange, setDateRange] = useState(() => getRange(DEFAULT_TIME_RANGE));
  const [order, setOrder] = useState("asc");
  const [apiData, setApiData] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [openChargerDetailsSlideout, setOpenChargerDetailsSlideout] = useState(false);
  const [filters, setFilters] = useState({
    msgType: "",
    feature: [],
    search: "",
    src: "",
  });
  const advancedFiltersLoaded = useRef(false);
  const [showLoader, setShowLoader] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const paginatedData = useMemo(() => {
    return apiData?.messages || [];
  }, [apiData]);

  const messageFilterInputSx = useMemo(
    () => ({
      "& .MuiOutlinedInput-root": {
        borderRadius: "8px",
        backgroundColor: isDark ? "#111827" : "#fff",
        fontSize: "0.8rem",
        color: isDark ? "#e5e7eb" : "#1f2937",
        "& fieldset": { borderColor: isDark ? "#374151" : "#e5e7eb" },
        "&:hover fieldset": { borderColor: isDark ? "#4b5563" : "#d1d5db" },
        "&.Mui-focused fieldset": { borderColor: "#f47c20", borderWidth: "1.5px" },
      },
      "& .MuiInputBase-input": {
        color: isDark ? "#e5e7eb" : "#1f2937",
        "&::placeholder": { color: isDark ? "#6b7280" : "#9ca3af", opacity: 1 },
      },
    }),
    [isDark]
  );

  useEffect(() => {
    setSerialNumber(globalSerialNumber);
  }, [globalSerialNumber]);

  useEffect(() => {
    if (globalDateRange) setDateRange(globalDateRange);
  }, [globalDateRange]);

  useEffect(() => {
    if (advancedFiltersLoaded.current) return;
    advancedFiltersLoaded.current = true;

    try {
      const savedFilters = getLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE) || {};

      setFilters(prev => ({
        ...prev,
        msgType: savedFilters.msgType || "",
        feature: Array.isArray(savedFilters.feature) ? savedFilters.feature : [],
        search: savedFilters.search || "",
      }));
    } catch {
      // localStorage restore failed
    }
  }, []);

  useEffect(() => {
    if (!advancedFiltersLoaded.current) return;

    try {
      patchLocalStorage(LOCAL_STORAGE_KEYS.FILTERBAR_STATE, {
        msgType: filters.msgType || "",
        feature: Array.isArray(filters.feature) ? filters.feature : [],
        search: filters.search || "",
      });
    } catch {
      // localStorage persist failed
    }
  }, [filters.msgType, filters.feature, filters.search]);

  const totalPages = Math.ceil((apiData.totalCount || 0) / rowsPerPage) || 0;

  const handleSortToggle = () => {
    setOrder(prev => {
      const newOrder = prev === "asc" ? "desc" : "asc";
      setFilters(f => {
        if (newOrder === "desc") {
          return { ...f, sortBy: "ts" };
        } else {
          // eslint-disable-next-line no-unused-vars
          const { sortBy, ...rest } = f;
          return rest;
        }
      });
      return newOrder;
    });
  };

  const cleanFilterObject = filters => {
    const cleaned = {};

    Object.entries(filters).forEach(([key, value]) => {
      if (!value) return; // skip empty fields

      if (key === "msgType" && MESSAGE_CONFIG[value]) {
        const mapped = MESSAGE_CONFIG[value];

        cleaned.src = mapped.source;
        cleaned.msgType = mapped.msgType;
      } else if (key === "feature") {
        if (Array.isArray(value) && value.length > 0) {
          cleaned[key] = value;
        }
        return;
      } else {
        cleaned[key] = value;
      }
    });

    return cleaned;
  };
  useEffect(() => {
    if (serialNumber) {
      fetchMessages(serialNumber, dateRange, page + 1, rowsPerPage, filters);
    } else {
      setShowLoader(false);
    }
  }, [serialNumber, dateRange, page, rowsPerPage, filters, order]);

  async function fetchMessages(serialNumber, dateRange, page, rowsPerPage, filters) {
    setShowLoader(true);
    try {
      const res = await fetchOcppMessages(
        serialNumber,
        new Date(dateRange[0]).toISOString(),
        new Date(dateRange[1]).toISOString(),
        page,
        rowsPerPage,
        cleanFilterObject(filters)
      );
      setApiData({
        ...res,
        messages: res?.messages ? [...res.messages] : [],
      });
    } catch (e) {
      setApiData({});
    }
    setShowLoader(false);
  }

  const openModal = (payload, idx) => {
    setModalPayload(payload);
    setModalIndex(idx);
    setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen) return;
    const handleKeyDown = e => {
      if (e.key === "ArrowUp" && modalIndex > 0) {
        const prev = apiData && apiData.messages[modalIndex - 1];
        if (prev) {
          setModalPayload(prev);
          setModalIndex(modalIndex - 1);
        }
      } else if (e.key === "ArrowDown" && modalIndex < apiData.messages.length - 1) {
        const next = apiData && apiData.messages[modalIndex + 1];
        if (next) {
          setModalPayload(next);
          setModalIndex(modalIndex + 1);
        }
      } else if (e.key === "Escape") {
        setModalOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalOpen, modalIndex, apiData.messages]);

  const handlePageChange = newPage => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = newRowsPerPage => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const ocpp_message_columns = [
    {
      key: "ts",
      label: "Timestamp",
      width: "200px",
      sortable: true,
      filterable: false,
      render: value => (siteTimezone ? dayjs(value).tz(siteTimezone) : dayjs(value)).format("YYYY-MM-DD HH:mm:ss.SSS"),
    },
    {
      key: "msgType",
      label: "Type",
      width: "120px",
      sortable: false,
      filterRender: () => (
        <FormControl size="small" fullWidth>
          <Select
            displayEmpty
            value={filters.msgType || ""}
            onChange={e => {
              setFilters(prev => ({ ...prev, msgType: e.target.value }));
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  mt: 0.5,
                  borderRadius: "12px",
                  backgroundColor: isDark ? "#1f2937" : "#fff",
                  border: isDark ? "1px solid #374151" : "1px solid #e5e7eb",
                  boxShadow: isDark ? "0 12px 36px rgba(0, 0, 0, 0.35)" : "0 12px 36px rgba(15, 23, 42, 0.12)",
                  "& .MuiMenuItem-root": {
                    fontSize: "0.8rem",
                    minHeight: 42,
                    color: isDark ? "#e5e7eb" : "#1f2937",
                  },
                },
              },
            }}
            sx={{
              borderRadius: "8px",
              backgroundColor: isDark ? "#111827" : "#fff",
              fontSize: "0.8rem",
              height: 36,
              color: isDark ? "#e5e7eb" : "#1f2937",
              "& .MuiSelect-icon": { color: isDark ? "#9ca3af" : "#6b7280" },
              "& fieldset": { borderColor: isDark ? "#374151" : "#e5e7eb" },
              "&:hover fieldset": { borderColor: isDark ? "#4b5563" : "#d1d5db" },
              "&.Mui-focused fieldset": { borderColor: "#f47c20", borderWidth: "1.5px" },
            }}
            renderValue={val => {
              if (!val) return <span style={{ color: isDark ? "#6b7280" : "#9ca3af", fontSize: "0.8rem" }}>Type</span>;
              const config = MESSAGE_CONFIG[val];
              return (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: "0.8rem",
                    color: isDark ? "#e5e7eb" : "#1f2937",
                  }}
                >
                  {config && <Icon src={config.icon} width={16} height={16} fill={config.fill} />}
                  <span>{config?.label ?? val}</span>
                </span>
              );
            }}
          >
            <MenuItem value="" sx={{ fontSize: "0.8rem", fontStyle: "normal", color: isDark ? "#e5e7eb" : "#1f2937" }}>
              <span style={{ fontSize: "0.8rem", fontStyle: "normal" }}>All</span>
            </MenuItem>
            {Object.entries(MESSAGE_CONFIG).map(([key, config]) => (
              <MenuItem
                key={key}
                value={key}
                sx={{ fontSize: "0.8rem", gap: 1, color: isDark ? "#e5e7eb" : "#1f2937" }}
              >
                <Icon src={config.icon} width={18} height={18} fill={config.fill} style={{ marginRight: 6 }} />
                {config.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ),
      render: (value, row) => renderMsgType(value, row.src),
    },
    {
      key: "feature",
      label: "Feature",
      width: "215px",
      sortable: false,
      filterRender: () => (
        <FeatureFilterSelect
          options={FEATURE_OPTIONS}
          value={filters.feature || []}
          onChange={newVal => {
            setFilters(prev => ({ ...prev, feature: newVal }));
          }}
        />
      ),
      render: value => <FeatureChip feature={value} />,
    },
    {
      key: "msg",
      label: "Message",
      width: "auto",
      sortable: false,
      filterRender: () => (
        <TextField
          size="small"
          placeholder="Search message..."
          value={filters.search || ""}
          onChange={e => {
            setFilters(prev => ({ ...prev, search: e.target.value }));
          }}
          fullWidth
          sx={messageFilterInputSx}
        />
      ),
      render: value => <MessageCell payload={value} />,
    },
  ];

  return (
    <div className="ocpp-page">
      <div className="ocpp-page__content">
        {!embedded && <h1 className="text-2xl mb-2 font-bold">OCPP Messages</h1>}
        {!embedded && (
          <ChargerDisplay serialNumber={serialNumber} onClick={() => setOpenChargerDetailsSlideout(true)} />
        )}
        <div className="evs-ocpp-header">
          <DateRangeToolbar
            activePreset={activePreset}
            onPresetChange={onPresetChange}
            onCustomChange={onCustomChange}
            showTimezone
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
            }}
          >
            <Button
              size="small"
              variant="outlined"
              onClick={() => setShowFilters(prev => !prev)}
              startIcon={<SlidersHorizontal size={15} />}
              sx={{
                textTransform: "none",
                borderColor: showFilters ? "#f47c20" : isDark ? "#374151" : "#d1d5db",
                borderRadius: "10px",
                color: showFilters ? "#f47c20" : isDark ? "#d1d5db" : "#374151",
                backgroundColor: showFilters ? "rgba(244, 124, 32, 0.06)" : isDark ? "#151b28" : "transparent",
                fontWeight: 600,
                fontSize: "0.8rem",
                px: 2,
                py: 0.7,
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: "#f47c20",
                  backgroundColor: isDark ? "rgba(244, 124, 32, 0.08)" : "rgba(244, 124, 32, 0.04)",
                  color: "#f47c20",
                },
              }}
            >
              {showFilters ? "Hide Filters" : "Show Filters"}
            </Button>
            {onRefresh && <RefreshButton onClick={onRefresh} tooltip="Refresh logs" />}
          </Box>
        </div>

        <DataTable
          columns={ocpp_message_columns}
          rows={paginatedData}
          isLoading={showLoader}
          onRowClick={openModal}
          highlightedRowIndex={modalOpen ? modalIndex : null}
          sortConfig={{ key: "ts", order }}
          onSortToggle={handleSortToggle}
          showFilters={showFilters}
          onFilter={() => {}}
          noDataHeight="45vh"
        />
        <Pagination
          totalPages={totalPages}
          currentPage={page}
          onPageChange={handlePageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleRowsPerPageChange}
          totalItems={apiData.totalCount}
          itemLabel="messages"
        />

        <MessageModal open={modalOpen} messages={modalPayload} onClose={() => setModalOpen(false)} />

        {modalOpen && (
          <div
            style={{
              position: embedded ? "static" : "fixed",
              bottom: 32,
              left: 0,
              right: 0,
              textAlign: "center",
              pointerEvents: embedded ? "auto" : "none",
              zIndex: embedded ? "auto" : 1301,
              marginTop: embedded ? 12 : 0,
            }}
          >
            <span
              style={{
                background: "#1f2937",
                padding: "8px 20px",
                borderRadius: 8,
                fontSize: 14,
                color: "#f9fafb",
                boxShadow: "0 4px 6px rgba(0,0,0,0.1)",
              }}
            >
              Use ↑ / ↓ arrows to navigate messages, Esc to close
            </span>
          </div>
        )}
        {openChargerDetailsSlideout && (
          <SlideOutPanel open={openChargerDetailsSlideout} onClose={() => setOpenChargerDetailsSlideout(false)} />
        )}
      </div>
    </div>
  );
}

OcppMessages.propTypes = {
  embedded: PropTypes.bool,
  activePreset: PropTypes.string,
  onPresetChange: PropTypes.func,
  onCustomChange: PropTypes.func,
  onRefresh: PropTypes.func,
};
